import { Col, Form } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import i18n from "i18next";
import { type FC, useMemo } from "react";

import type { TerminalBatchUpdateEntity } from "src/app/features/Terminal/types/TerminalBatchUpdateEntity";
import { TerminalType } from "src/app/features/TerminalProfile/types/TerminalProfileType";
import { DrawerForm } from "../../../../../components/DrawerForm/DrawerForm";
import { BatchUpdateFormRow } from "../../../../../components/DrawerForm/drawerFormStyles";
import { FieldLabel } from "../../../../../components/DrawerForm/FieldLabel";
import { Checkbox, SelectDynamic, TextInput } from "../../../../../components/Form/formComponents";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import type { SearchFormProps } from "../../../../../components/Form/types/types";
import { terminalProfileIPTSelectConfig } from "../../../../../components/SearchForm/FormSearchUtils";

type TerminalBatchUpdateFormProps = SearchFormProps<TerminalBatchUpdateEntity> & {
    form?: FormInstance;
    showOptTerminalProfileField?: boolean;
};

// Field names as they appear in the Terminal form config from BE, where they differ from our TerminalBatchUpdateEntity keys.
const TERMINAL_BATCH_UPDATE_FORM_FIELD_NAME_OVERRIDES: Partial<Record<keyof TerminalBatchUpdateEntity, string>> = {
    batchName: "name",
    updateBatchStatus: "status",
};

export const TerminalBatchUpdateForm: FC<TerminalBatchUpdateFormProps> = ({ searchCallback, form, showOptTerminalProfileField }) => {
    const { data: formDefinition } = useLoadFormDefinitionQuery("terminal");

    // Only expose fields that exist in the current Data Group's Terminal form config.
    const availableFieldNames = useMemo(
        () => new Set(formDefinition?.formConfiguration?.tabs.flatMap((tab) => tab.fields.map((field) => field.name)) ?? []),
        [formDefinition],
    );

    const isFieldAvailable = (fieldName: keyof TerminalBatchUpdateEntity) =>
        availableFieldNames.has(TERMINAL_BATCH_UPDATE_FORM_FIELD_NAME_OVERRIDES[fieldName] ?? fieldName);

    return (
        <DrawerForm onSubmit={searchCallback} form={form} useSubmitButton={true} submitLabel={i18n.t("button.batchUpdate")}>
            <p>{i18n.t("batchUpdate.message")}</p>

            {isFieldAvailable("batchName") && (
                <>
                    <FieldLabel label={i18n.t("terminal.name")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="batchName">
                                <TextInput name="batchName" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateBatchName" valuePropName="checked">
                                <Checkbox name="updateBatchName" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("updateBatchStatus") && (
                <>
                    <FieldLabel label={i18n.t("terminal.status")} />

                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="batchStatus" valuePropName="checked">
                                <Checkbox name="batchStatus" label="" readOnly={true} defaultChecked={true} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateBatchStatus" valuePropName="checked">
                                <Checkbox
                                    name="updateBatchStatus"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {showOptTerminalProfileField && isFieldAvailable("terminalProfileId") && (
                <>
                    <FieldLabel label={i18n.t("terminal.batchUpdate.optTerminalProfile")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="terminalProfileId">
                                <SelectDynamic
                                    name="terminalProfileId"
                                    label={""}
                                    fieldConfig={terminalProfileIPTSelectConfig(TerminalType.ISOPT)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateBatchTerminalProfile" valuePropName="checked">
                                <Checkbox
                                    name="updateBatchTerminalProfile"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}
        </DrawerForm>
    );
};
